import styled from 'styled-components';
import { theme } from '../theme';

// Modal Overlay
export const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.fast};
  padding: ${theme.spacing[4]};
`;

// Modal Content
export const ModalContent = styled.div<{ 
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  isOpen?: boolean;
}>`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows['2xl']};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: transform ${theme.transitions.fast};
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return `max-width: 400px;`;
      case 'md':
        return `max-width: 500px;`;
      case 'lg':
        return `max-width: 800px;`;
      case 'xl':
        return `max-width: 1200px;`;
      case 'full':
        return `
          max-width: calc(100vw - ${theme.spacing[8]});
          max-height: calc(100vh - ${theme.spacing[8]});
        `;
      default:
        return `max-width: 500px;`;
    }
  }}
`;

// Modal Header
export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border.light};
`;

export const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

export const ModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.base};
  transition: all ${theme.transitions.fast};
  
  &:hover {
    background-color: ${theme.colors.gray[100]};
    color: ${theme.colors.text.primary};
  }
  
  &:focus {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

// Modal Body
export const ModalBody = styled.div`
  padding: ${theme.spacing[6]};
`;

// Modal Footer
export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.light};
`;

// Drawer (Side Modal)
export const DrawerOverlay = styled(ModalOverlay)``;

export const DrawerContent = styled.div<{
  position?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  isOpen?: boolean;
}>`
  position: fixed;
  top: 0;
  bottom: 0;
  ${props => props.position === 'right' ? 'right: 0;' : 'left: 0;'}
  background: ${theme.colors.background.primary};
  box-shadow: ${theme.shadows.xl};
  transform: translateX(${props => {
    if (!props.isOpen) {
      return props.position === 'right' ? '100%' : '-100%';
    }
    return '0';
  }});
  transition: transform ${theme.transitions.normal};
  z-index: ${theme.zIndex.modal};
  overflow-y: auto;
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return `width: 320px;`;
      case 'md':
        return `width: 480px;`;
      case 'lg':
        return `width: 640px;`;
      default:
        return `width: 480px;`;
    }
  }}
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100vw;
  }
`;

// Toast/Notification
export const ToastContainer = styled.div`
  position: fixed;
  top: ${theme.spacing[4]};
  right: ${theme.spacing[4]};
  z-index: ${theme.zIndex.toast};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-width: 400px;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    left: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    max-width: none;
  }
`;

export const Toast = styled.div<{
  variant?: 'success' | 'error' | 'warning' | 'info';
  isVisible?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  border-left: 4px solid ${props => {
    switch (props.variant) {
      case 'success':
        return theme.colors.success[500];
      case 'error':
        return theme.colors.error[500];
      case 'warning':
        return theme.colors.warning[500];
      case 'info':
        return theme.colors.primary[500];
      default:
        return theme.colors.gray[500];
    }
  }};
  transform: translateX(${props => props.isVisible ? '0' : '100%'});
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: all ${theme.transitions.normal};
`;

export const ToastContent = styled.div`
  flex: 1;
`;

export const ToastTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

export const ToastMessage = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

// Popover
export const PopoverContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const PopoverContent = styled.div<{
  position?: 'top' | 'bottom' | 'left' | 'right';
  isOpen?: boolean;
}>`
  position: absolute;
  z-index: ${theme.zIndex.popover};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[3]};
  min-width: 200px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: all ${theme.transitions.fast};
  
  ${props => {
    switch (props.position) {
      case 'top':
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%) ${props.isOpen ? 'scale(1)' : 'scale(0.95)'};
          margin-bottom: ${theme.spacing[2]};
        `;
      case 'bottom':
        return `
          top: 100%;
          left: 50%;
          transform: translateX(-50%) ${props.isOpen ? 'scale(1)' : 'scale(0.95)'};
          margin-top: ${theme.spacing[2]};
        `;
      case 'left':
        return `
          right: 100%;
          top: 50%;
          transform: translateY(-50%) ${props.isOpen ? 'scale(1)' : 'scale(0.95)'};
          margin-right: ${theme.spacing[2]};
        `;
      case 'right':
        return `
          left: 100%;
          top: 50%;
          transform: translateY(-50%) ${props.isOpen ? 'scale(1)' : 'scale(0.95)'};
          margin-left: ${theme.spacing[2]};
        `;
      default:
        return `
          top: 100%;
          left: 50%;
          transform: translateX(-50%) ${props.isOpen ? 'scale(1)' : 'scale(0.95)'};
          margin-top: ${theme.spacing[2]};
        `;
    }
  }}
`;

// Tooltip
export const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const TooltipContent = styled.div<{
  position?: 'top' | 'bottom' | 'left' | 'right';
  isVisible?: boolean;
}>`
  position: absolute;
  z-index: ${theme.zIndex.tooltip};
  background: ${theme.colors.gray[900]};
  color: ${theme.colors.text.inverse};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.base};
  font-size: ${theme.typography.fontSize.sm};
  white-space: nowrap;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.fast};
  pointer-events: none;
  
  ${props => {
    switch (props.position) {
      case 'top':
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: ${theme.spacing[2]};
        `;
      case 'bottom':
        return `
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: ${theme.spacing[2]};
        `;
      case 'left':
        return `
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-right: ${theme.spacing[2]};
        `;
      case 'right':
        return `
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-left: ${theme.spacing[2]};
        `;
      default:
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: ${theme.spacing[2]};
        `;
    }
  }}
`;
